import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://floridahousesforsale.deals';

const sellSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Sell Your Florida Home | Seller\'s Guide | Amparo Jarquin',
  description: 'Learn how to sell your Florida home with Amparo Jarquin. Expert guidance on pricing, marketing, negotiation, and closing your Florida property sale.',
  url: `${SITE_URL}/sell`,
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Sell', item: `${SITE_URL}/sell` },
    ],
  },
};

const steps = [
  {
    number: '1',
    title: 'DETERMINE THE MARKET VALUE OF YOUR HOME',
    description:
      "The first step is critical to the process. You can see what similar homes sell for in your market. Many factors determine a home's value including its location, condition, age, recent updates, features, and comparable sales in the neighborhood. Amparo is an expert in finding the true value of your Florida property.",
  },
  {
    number: '2',
    title: 'PREPARE YOUR HOME FOR THE MARKET',
    description:
      "We are connoisseurs of the art of maximizing the equity of your home. Amparo will advise you on any improvements, home repairs, or even professional staging that best prepares your Florida home to return its maximum value. She knows how to present your home so it will sell quickly and at the right price.",
  },
  {
    number: '3',
    title: 'ATTRACT APPROPRIATE BUYERS',
    description:
      'Once your home is ready, Amparo will provide the highest level of local, regional, and global marketing. Your personal marketing plan may include video, print, open houses, private showings, and anything else we believe will benefit your sale. Each plan is custom designed to attract the right Florida buyers.',
  },
  {
    number: '4',
    title: 'NEGOTIATE THE WINNING OFFER',
    description:
      'Once you receive an offer or multiple offers, your agent employs smart strategies based on real experience. Amparo is skilled at negotiating the strongest offer so that you not only score the best price but also the best terms and conditions on your Florida home sale.',
  },
  {
    number: '5',
    title: 'CLOSING',
    description:
      "We utilize the latest technology to help make your selling experience seamless and convenient. Once your home goes under contract, Amparo will manage every aspect of the transaction, keeping you updated on each step from home inspection to purchase and sale, mortgage approval, and everything else in between. You'll stay well informed all the way to closing day.",
  },
];

export default function SellPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Sell Your Home in Florida | Seller's Guide | Amparo Jarquin REALTOR®"
        description="Ready to sell your Florida home? Amparo Jarquin provides expert pricing, marketing, and negotiation to get the best value for your Florida property."
        keywords="sell home Florida, Florida home selling guide, list home Florida, Florida seller agent, sell house West Palm Beach"
        canonical="/sell"
        schema={sellSchema}
      />
      <Navbar />

      {/* Hero Banner */}
      <section className="relative h-72 md:h-96 flex items-center justify-center overflow-hidden mt-0">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/6f3a14b8a8483c1b84474420134c6f1e.jpg"
            alt="Sell your Florida home"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/55"></div>
        </div>
        <div className="relative z-10 text-center px-6 w-full">
          <p className="text-amber-300 text-xs font-semibold tracking-widest uppercase mb-3">
            Amparo Jarquin Real Estate
          </p>
          <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight">
            Sell Your <span className="text-amber-400">Florida Home</span>
          </h1>
          <p className="text-white/75 text-base md:text-lg max-w-xl mx-auto mt-4">
            Expert guidance, proven strategy, and dedicated support — from listing to closing.
          </p>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-20">
            <p className="text-amber-600 text-xs font-semibold tracking-widest uppercase mb-3">The Selling Process</p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">How We Sell Your Home</h2>
            <div className="w-12 h-1 bg-amber-500 mx-auto mt-6"></div>
          </div>

          <div className="space-y-20">
            {steps.map((step) => (
              <div
                key={step.number}
                className="flex flex-col md:flex-row items-start gap-8 group"
              >
                {/* Big Number */}
                <div className="flex-shrink-0">
                  <span
                    className="text-[120px] md:text-[160px] font-bold leading-none select-none transition-colors duration-300 group-hover:text-amber-500"
                    style={{
                      color: 'transparent',
                      WebkitTextStroke: '2px #1c3a5e',
                    }}
                  >
                    {step.number}
                  </span>
                </div>
                {/* Content */}
                <div className="pt-4 md:pt-8">
                  <h3 className="text-base font-bold text-gray-900 tracking-wide mb-4">{step.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-amber-50">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Ready to List Your Home?</h2>
          <p className="text-gray-600 text-base mb-8 leading-relaxed">
            Let Amparo guide you through every step of the selling process and get the best value for your Florida property.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="px-8 py-4 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-full transition-colors whitespace-nowrap cursor-pointer text-sm tracking-wide"
            >
              Contact Amparo
            </Link>
            <Link
              to="/request"
              className="px-8 py-4 border-2 border-amber-500 text-amber-600 hover:bg-amber-500 hover:text-white font-semibold rounded-full transition-colors whitespace-nowrap cursor-pointer text-sm tracking-wide"
            >
              Request Information
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
